import Airport from "@website/icons/components/airport";
import History from "@website/icons/components/history";
import Origin from "@website/icons/components/origin";
import { MaterialIcon } from "@website/icons/material";
import NoResult from "@website/ticket-ui/common/components/no-result";
import { PassengerCountOld } from "@website/ticket-ui/common/components/passenger-count-old";
import { SaleTypeOld } from "@website/ticket-ui/common/components/sale-type-old";
import { useDate } from "@website/ticket-ui/common/hooks/search-panel/use-date";
import { usePassengerCount } from "@website/ticket-ui/common/hooks/search-panel/use-passenger-count";
import { useSaleType } from "@website/ticket-ui/common/hooks/search-panel/use-sale-type";
import { useValidation } from "@website/ticket-ui/common/hooks/search-panel/use-validation";
import { AirportsItemType } from "@website/ticket-ui/common/types";
import { useDestination } from "@website/ticket-ui/dom-flight-search-panel-old/hooks/use-destination";
import { useHistory } from "@website/ticket-ui/dom-flight-search-panel-old/hooks/use-history";
import { useOrigin } from "@website/ticket-ui/dom-flight-search-panel-old/hooks/use-origin";
import { useSubmit } from "@website/ticket-ui/dom-flight-search-panel-old/hooks/use-submit";
import {
  SearchPageQueryParameters,
  SearchPanelParameters
} from "@website/ticket-ui/dom-flight-search-panel-old/types";
import {
  createOriginDestinationHistorySubtitle,
  createOriginDestinationHistoryTitle,
  createOriginDestinationItemSubtitle
} from "@website/ticket-ui/dom-flight-search-panel-old/utils/createSubtitle";
import { useFlightClass } from "@website/ticket-ui/inter-flight-search-panel/hooks/use-flight-class";
import { AutoComplete } from "@website/ui-old/auto-complete";
import { Button } from "@website/ui-old/button";
import { Datepicker } from "@website/ui-old/datepicker";
import { DatepickerSelectedDateType } from "@website/ui/datepicker";
import { deepCompare } from "@website/utils/deep-compare";
import { useRouter } from "next/router";
import { memo, useCallback, useMemo } from "react";

const SearchPanelDomFlightsOld = (parameters: SearchPanelParameters) => {
  const { onClose, destinationSearchObject, originSearchObject, searchSource } =
    parameters;
  const router = useRouter();
  const searchInitialValues = {
    saleType: router.query?.returnDate ? "round-trip" : "one-way",
    flightClass: router.query?.cabinType as string,
    passengerCount: {
      adult: Number(router.query?.adultCount) || 1,
      child: Number(router.query?.childCount) || 0,
      infant: Number(router.query?.infantCount) || 0
    }
  };

  const {
    selectedOriginItem,
    selectedOriginText,
    originQuery,
    onChangeOrigin,
    onChangeOriginQuery,
    onSelectOrigin,
    originLoading,
    originLabel,
    originList,
    originIsLoading,
    originData
  } = useOrigin(originSearchObject);
  const {
    selectedDestinationItem,
    selectedDestinationText,
    destinationQuery,
    onChangeDestination,
    onChangeDestinationQuery,
    onSelectDestination,
    destinationLoading,
    destinationLabel,
    destinationList,
    destinationIsLoading,
    destinationData
  } = useDestination(destinationSearchObject);
  const { date, calendarLocale, onChangeDate, fromTitle, toTitle } = useDate(
    router.query as unknown as SearchPageQueryParameters
  );

  const {
    addToHistory,
    onSelectDestinationRouteHistory,
    onSelectOriginRouteHistory,
    history
  } = useHistory(onChangeOrigin, onChangeDestination);
  const { saleType, saleTypeList, onChangeSaleType, onChangeDatepickerMode } =
    useSaleType(searchInitialValues.saleType);

  const {
    onSubmitPassengerCount,
    passengerCount,
    passengerCountList,
    passengerCountResult,
    passengerCountTitle
  } = usePassengerCount(searchInitialValues.passengerCount);

  const onSwap = useCallback(() => {
    onChangeOrigin(selectedDestinationItem);
    onChangeDestination(selectedOriginItem);
  }, [selectedOriginItem, selectedDestinationItem]);

  const { originError, destinationError, dateError, submitValidationCheck } =
    useValidation({
      originItem: selectedOriginItem,
      destinationItem: selectedDestinationItem,
      saleType,
      date
    });

  const { flightClass } = useFlightClass(searchInitialValues.flightClass);

  const { onSubmitClick, submitLoading, submitText } = useSubmit(
    selectedOriginItem,
    selectedDestinationItem,
    saleType,
    date as DatepickerSelectedDateType,
    calendarLocale,
    passengerCount,
    flightClass,
    addToHistory,
    submitValidationCheck,
    searchSource
  );

  const createRouteHistoryList = (key: "origin" | "destination") =>
    history.map((item) => {
      const location = item?.[key];
      return {
        id: `${location?.iataCode}-${location?.cityName}`,
        title: createOriginDestinationHistoryTitle(location),
        subtitle: createOriginDestinationHistorySubtitle(location),
        StartIcon: <History />
      };
    });

  const originRouteHistoryList = useMemo(
    () => createRouteHistoryList("origin"),
    [history]
  );
  const destinationRouteHistoryList = useMemo(
    () => createRouteHistoryList("destination"),
    [history]
  );

  const locationListNormalized = (
    locationList: Array<AirportsItemType> | undefined
  ) => {
    const list = [];
    if (locationList)
      for (const city of locationList) {
        if (city.includeAllAirports) {
          list.push({
            id: `${city.iataCode}-${city.cityName}`,
            title: `${city.cityFaName}، ${city.countryFaName}`,
            subtitle: createOriginDestinationItemSubtitle(city),
            StartIcon: (
              <div className="fill-icons-regent-gray">
                <Origin />
              </div>
            ),
            indent: 0
          });
        }

        if (
          (city.airports?.length > 1 && !city?.isSuggested) ||
          city.includeAllAirports === false
        ) {
          for (const airport of city.airports) {
            list.push({
              id: `${airport.iataCode}-${airport.name}-${city.cityName}`,
              title: airport.faName || `فرودگاه  ${airport.name}`,
              subtitle: `${city.cityFaName} - ${airport.iataCode}`,
              indent: city.includeAllAirports ? 1 : 0,
              StartIcon: <Airport />
            });
          }
        }
      }
    return list;
  };

  const destinationListNormalized = useMemo(
    () => locationListNormalized(destinationList),
    [destinationList]
  );

  const originListNormalized = useMemo(
    () => locationListNormalized(originList),
    [originList]
  );

  const noResult = (
    query: string,
    data: Array<AirportsItemType> | undefined,
    loading: boolean
  ) =>
    query?.length >= 3 && !loading && data && data.length === 0 ? (
      <NoResult count="14,000" query={query} />
    ) : undefined;

  const DestinationNoResult = useMemo(
    () => noResult(destinationQuery, destinationData, destinationIsLoading),
    [destinationQuery, destinationData, destinationIsLoading]
  );
  const OriginNoResult = useMemo(
    () => noResult(originQuery, originData, originIsLoading),
    [originQuery, originData, originIsLoading]
  );

  return (
    <div className="relative mt-4 flex h-full flex-col gap-8 lg:mt-0 lg:h-auto">
      {searchSource !== "nowrouzLanding" && (
        <div className="flex items-center justify-between gap-2">
          <div
            className="flex flex-row gap-2 max-md:overflow-x-auto"
            data-testid="filters-section"
          >
            <SaleTypeOld
              list={saleTypeList}
              name="flight-domestic-sale-type"
              onChange={onChangeSaleType}
              value={saleType}
            />
            <PassengerCountOld
              list={passengerCountList}
              onSubmit={onSubmitPassengerCount}
              result={passengerCountResult}
              title={passengerCountTitle}
            />
          </div>
          {searchSource !== "landing" && (
            <div className="hidden lg:block">
              <Button
                StartIcon={<MaterialIcon name="close" />}
                color="neutral"
                onClick={onClose}
                size="small"
                variant="text"
              />
            </div>
          )}
        </div>
      )}

      <div className="flex flex-1 flex-col gap-4 lg:flex-row lg:items-start">
        <div
          className={
            searchSource === "nowrouzLanding" ? "lg:flex-[1.5]" : "lg:flex-[3]"
          }
        >
          <AutoComplete
            error={originError || destinationError}
            fullWidth
            hasTitleLabel={searchSource === "nowrouzLanding"}
            hideBorders={searchSource === "nowrouzLanding"}
            hideIcons={searchSource === "nowrouzLanding"}
            onSwap={searchSource === "nowrouzLanding" ? undefined : onSwap}
            parts={[
              {
                label: originLabel,
                list: originListNormalized,
                historyList: originRouteHistoryList,
                historyTitle: "آخرین جستجوهای شما",
                text: selectedOriginText,
                onSelect: onSelectOrigin,
                onSelectHistory: onSelectOriginRouteHistory,
                onChangeQuery: onChangeOriginQuery,
                query: originQuery,
                loading: originLoading,
                NoResult: OriginNoResult
              },
              {
                label: destinationLabel,
                list: destinationListNormalized,
                historyList: destinationRouteHistoryList,
                historyTitle: "آخرین جستجوهای شما",
                text: selectedDestinationText,
                onSelect: onSelectDestination,
                onSelectHistory: onSelectDestinationRouteHistory,
                onChangeQuery: onChangeDestinationQuery,
                query: destinationQuery,
                loading: destinationLoading,
                NoResult: DestinationNoResult
              }
            ]}
          />
        </div>
        <div
          className={
            searchSource === "nowrouzLanding" ? "lg:flex-[2.2]" : "lg:flex-[2]"
          }
        >
          <Datepicker
            changeableMode
            error={dateError.length > 0}
            fromTitle={fromTitle}
            fullWidth
            hasTitleLabel={searchSource === "nowrouzLanding"}
            helperText={dateError}
            hideBorders={searchSource === "nowrouzLanding"}
            hideIcons={searchSource === "nowrouzLanding"}
            initialCalendarLocale={
              calendarLocale === "persian" ? "persian" : "gregorian"
            }
            mode={saleType === "one-way" ? "single" : "start-end"}
            onChange={onChangeDate}
            secondaryMode="same-start-end"
            setMode={onChangeDatepickerMode}
            toTitle={toTitle}
            value={date as DatepickerSelectedDateType}
          />
        </div>

        {searchSource === "nowrouzLanding" && (
          <div
            className={
              searchSource === "nowrouzLanding"
                ? "lg:flex-[0.7]"
                : "lg:flex-[1]"
            }
          >
            <PassengerCountOld
              attached
              list={passengerCountList}
              onSubmit={onSubmitPassengerCount}
              result={passengerCountResult}
              title={passengerCountTitle}
            />
          </div>
        )}

        <div
          className={`absolute bottom-0 w-full lg:relative lg:flex lg:w-auto lg:items-end ${
            searchSource === "landing" ? "lg:min-w-[134px]" : ""
          }`}
        >
          <Button
            datatestid="search-button"
            fullWidth
            loading={submitLoading}
            onClick={onSubmitClick}
            size="x-large"
          >
            {submitText}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default memo(SearchPanelDomFlightsOld, deepCompare);
